// src/modules/operaciones/inventarios/utils/exportarExcel.js
//
// Exportación a Excel (XLSX) de los ajustes: cabecera, filas de detalle y libro completo.
import { fmt, fmtFecha } from "../../../../utils/format"; // Números y fechas compartidos
import { TEXTO_ALERTA_EXCEL, txt, lineasDe, fmtValor } from "./constantes"; // Texto de alerta y helpers
import { infoPrecio } from "./reglasPrecio"; // Precio calculado o alerta por línea

/* ---- Exportación a Excel ---- */

// Mapa de columna de BD → etiqueta legible en el Excel (define también el orden)
const ETIQUETAS_DETALLE = {
  AJUS_ITEM: "Item", // Número de ítem
  ARTI_COD: "Código Artículo", // Código del artículo
  AJUS_CODBAR: "Código Barras", // Código de barras
  AJDE_DESC: "Descripción", // Descripción del artículo
  BODE_COD: "Bodega", // Bodega
  AJUS_EXANT: "Existencia Antes", // Existencia anterior
  AJUS_EXNUE: "Existencia Nueva", // Existencia nueva
  AJUS_UNIDAD: "Unidad", // Unidad de medida
  AJUS_LOTE: "Lote", // Lote
  AJUS_FACTOR: "Factor", // Factor de conversión
  AJDE_COSTO: "Costo", // Costo (aquí se aplica el precio calculado)
  AJDE_ANULADO: "Anulado", // Indicador de anulado
  AJDE_OBS: "Observación", // Observación
  AJDE_LVENCE: "Vencimiento", // Fecha de vencimiento del lote
};

// Columnas del detalle que no se muestran en el modal
export const COLUMNAS_OCULTAS_DETALLE = new Set(["AJUS_LOTE", "AJUS_FACTOR", "AJDE_OBS", "AJDE_LVENCE"]);

// Limpia caracteres prohibidos de Excel en nombres de hoja y limita a 31 caracteres
const sanearNombreHoja = (nombre) => String(nombre).replace(/[\\/?*[\]:]/g, "-").slice(0, 31).trim() || "Ajuste";

// Genera un nombre de hoja que no exista aún en el libro
export const nombreHojaUnico = (wb, id) => {
  const base = sanearNombreHoja(id); // Nombre base saneado
  let nombre = base; // Nombre candidato
  for (let i = 2; wb.SheetNames.includes(nombre); i++) nombre = `${base.slice(0, 28)}-${i}`; // Añade sufijo hasta ser único
  return nombre; // Devuelve el nombre libre
};

// Convierte un ajuste en la fila de cabecera para la hoja resumen
const cabeceraAjuste = (a) => ({
  ID: a.AJUS_ID, // Id del ajuste
  Fecha: fmtFecha(a.AJUS_FECHA), // Fecha formateada
  Prefijo: txt(a.PREF_PRE), // Prefijo del documento
  Bodega: txt(a.BODE_COD), // Bodega
  Número: txt(a.AJUS_NUMERO), // Número del documento
  Concepto: txt(a.AJUS_CONC), // Concepto del ajuste
  Usuario: txt(a.AJUS_USUARIO), // Usuario que lo creó
  Anulado: txt(a.AJUS_ANULADO) === "S" ? "Sí" : "No", // Anulado en texto legible
});

// Construye las filas de la hoja de detalle aplicando precios y alertas
function filasDetalle(ajusteId, lineas, precios) {
  if (lineas.length === 0) { // Ajuste sin líneas
    return { filas: [{ Detalle: `El ajuste ${ajusteId} no tiene líneas de detalle en Sysplus.` }], recalculadas: 0, alertas: 0 }; // Fila informativa
  }
  let recalculadas = 0; // Cuenta costos reemplazados
  let alertas = 0; // Cuenta líneas con alerta
  const filas = lineas.map((d) => { // Transforma cada línea en fila de Excel
    const { pc, alerta } = infoPrecio(d, precios); // Obtiene precio o alerta de la línea
    if (pc) recalculadas++; // Suma si se recalculó
    if (alerta) alertas++; // Suma si hay alerta
    const fila = {}; // Fila de salida
    Object.entries(ETIQUETAS_DETALLE).forEach(([col, etiqueta]) => { // Recorre columnas en orden
      if (col === "AJDE_COSTO" && alerta) fila[etiqueta] = TEXTO_ALERTA_EXCEL; // En alerta escribe el texto de revisar
      else fila[etiqueta] = fmtValor(col, col === "AJDE_COSTO" && pc ? pc.precio : d[col]); // Usa precio calculado o el valor original
    });
    return fila; // Devuelve la fila armada
  });
  return { filas, recalculadas, alertas }; // Devuelve filas y contadores
}

// Libro: hoja resumen con la cabecera de cada ajuste + una hoja de detalle por ajuste.
export async function exportarLibro({ ajustes, detallePorAjuste, precios, archivo, hojaResumen, nombreHoja }) {
  const XLSX = await import("xlsx"); // Carga la librería xlsx solo cuando se necesita
  const wb = XLSX.utils.book_new(); // Crea un libro vacío
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ajustes.map(cabeceraAjuste)), hojaResumen); // Agrega la hoja resumen

  const total = { lineas: 0, recalculadas: 0, alertas: 0 }; // Acumulados de toda la exportación
  ajustes.forEach((ajuste) => { // Recorre cada ajuste
    const lineas = lineasDe(detallePorAjuste, ajuste); // Líneas del ajuste
    const { filas, recalculadas, alertas } = filasDetalle(ajuste.AJUS_ID, lineas, precios); // Filas y contadores del detalle
    total.lineas += lineas.length; // Suma líneas
    total.recalculadas += recalculadas; // Suma recalculadas
    total.alertas += alertas; // Suma alertas
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), nombreHoja(wb, ajuste.AJUS_ID)); // Agrega la hoja de detalle
  });

  XLSX.writeFile(wb, archivo); // Descarga el archivo Excel
  return total; // Devuelve los totales
}

// Arma el mensaje de resultado de exportación (warning si hubo alertas)
export const mensajeExcel = (texto, { recalculadas, alertas }) => ({
  type: alertas > 0 ? "warning" : "success", // Tipo según haya alertas
  text: `${texto}${recalculadas > 0 ? ` ${fmt(recalculadas, 3)} costos recalculados.` : ""}${
    alertas > 0 ? ` ⚠️ ${fmt(alertas, 3)} líneas con alerta.` : ""
  }`, // Texto con conteos opcionales
});